const formatFieldName = (field) => {
  if (field === "oem_ids" || field === "oem_id") {
    return "OEM";
  }
  if (field === "uom_ids" || field === "uom_id") {
    return "UOM";
  }
  return field
    .replace(/_(id|ids)$/, "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (char) => char.toUpperCase());
};
export const required = (value, field) => {
  const FieldName = formatFieldName(field);
  if (Array.isArray(value)) {
    return value.length === 0 ? `${FieldName} cannot be blank.` : "";
  }
  return !value ||
    value == "00:00:00" ||
    (typeof value === "string" && value?.trim() === "")
    ? `${FieldName} cannot be blank.`
    : "";
};

// Email validation
export const isEmail = (value, field) => {
  const FieldName = formatFieldName(field);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value)) {
    return `Enter valid ${FieldName}.`;
  }
  return "";
};

// Number validation
export const isNumber = (value, field) => {
  const FieldName = formatFieldName(field);

  if (isNaN(value)) {
    return `${FieldName} must be a valid number.`;
  }
  return "";
};
export const isCost = (value, field) => {
  const FieldName = formatFieldName(field);

  // Allow numbers with optional commas and a single dot for decimals
  const numberRegex = /^(\d{1,3}(,\d{3})*|\d+)(\.\d+)?$/;
  const stringValue = String(value);
  if (!numberRegex.test(value)) {
    return `${FieldName} must be a valid number.`;
  }
  const numericValue = parseFloat(stringValue.replace(/,/g, ""));

  if (numericValue === 0) {
    return `${FieldName} cannot be zero.`;
  }

  return "";
};

// Alphabet-only validation
export const isAlphabet = (value, field) => {
  const FieldName = formatFieldName(field);

  const alphabetRegex = /^[A-Za-z]+( [A-Za-z]+)*$/;

  const trimmedValue = value?.trim();

  if (/ {2,}/.test(value)) {
    return `${FieldName} cannot have multiple consecutive spaces.`;
  }

  if (value !== trimmedValue) {
    return `${FieldName} cannot have extra space. Add one more word after a space.`;
  }

  if (!alphabetRegex.test(trimmedValue)) {
    return `${FieldName} must contain only letters and single spaces between words.`;
  }

  return "";
};

export const isIconNameOrImage = (value, field) => {
  const FieldName = formatFieldName(field);

  const iconNameRegex = /^[a-zA-Z0-9]+(-[a-zA-Z0-9]+)+$/;

  const imageFileRegex = /^[a-zA-Z0-9_\-]+\.(png|jpg|jpeg|gif|svg|webp)$/i;

  if (!iconNameRegex.test(value) && !imageFileRegex.test(value)) {
    return `${FieldName} must be a valid icon name (e.g., home-icon) or a valid image file name with an extension (e.g., file_name.png, example-image.jpeg).`;
  }

  return "";
};

// Min length validation
export const minLength = (length) => {
  return (value, field) => {
    const FieldName = formatFieldName(field);
    const stringValue = String(value)?.trim();
    if (stringValue.length < length) {
      return `${FieldName} must be at least ${length} characters long.`;
    }
    return "";
  };
};

// Max length validation
export const maxLength = (length) => {
  return (value, field) => {
    const FieldName = formatFieldName(field);
    const stringValue = String(value)?.trim();
    if (stringValue.length > length) {
      return `${FieldName} must be no longer than ${length} characters.`;
    }
    return "";
  };
};

// Password validation
export const isPassword = (value, field) => {
  const FieldName = formatFieldName(field);
  const specialCharRegex = /[!@#$%^&*()\-_=+\|[\]{};:\/?.]/;
  if (!specialCharRegex.test(value)) {
    return `${FieldName} must contain at least one special character.`;
  }
  const numberRegex = /\d/;
  if (!numberRegex.test(value)) {
    return `${FieldName} must contain at least one number.`;
  }
  const alphabetRegex = /[A-Za-z]/;
  if (!alphabetRegex.test(value)) {
    return `${FieldName} must contain at least one letter.`;
  }
  if (value.length < 8) {
    return `${FieldName} must be at least 8 characters long.`;
  }
  return "";
};

// Phone number validation
export const isPhoneNumber = (value, field) => {
  const FieldName = formatFieldName(field);
  const phoneRegex = /^\d{10}$/;

  // Check if the value contains only digits and if it is not a sequence of all zeros
  if (!value?.match(/^\d+$/)) {
    return `${FieldName} must contain only digits.`;
  }
  if (!phoneRegex?.test(value)) {
    return `${FieldName} must be exactly 10 digits.`;
  }
  if (/^0+$/.test(value)) {
    // Check if the value is all zeros
    return `${FieldName} cannot be all zeros.`;
  }
  return "";
};

// Date validation
export const isValidDate = (value, field) => {
  const FieldName = formatFieldName(field);
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(value)) {
    return `${FieldName} must be in the proper yyyy-mm-dd format.`;
  }
  return "";
};

// Time validation
export const isValidTime = (value, field) => {
  const FieldName = formatFieldName(field);
  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;
  if (!timeRegex.test(value?.trim())) {
    return `${FieldName} must be in the proper hh:mm:ss format.`;
  }
  return "";
};

// Checkbox validation
export const isChecked = (value, field) => {
  const FieldName = formatFieldName(field);
  if (!value) {
    return `${FieldName} must be checked.`;
  }
  return "";
};

// URL validation
export const isURL = (value, field) => {
  const FieldName = formatFieldName(field);
  const urlRegex = /^(https?:\/\/)?([^\s.]+\.\S{2}|localhost[\:?\d]*)\S*$/;
  if (!urlRegex.test(value)) {
    return `${FieldName} must be a valid URL.`;
  }
  return "";
};
// URL path validation
export const isPath = (value, field) => {
  const FieldName = formatFieldName(field);
  const pathRegex = /^\/[a-zA-Z0-9\-]+(?:\/[a-zA-Z0-9\-]+)*$/;
  if (!pathRegex.test(value)) {
    return `${FieldName} must be a valid path url starting with '/' and containing only letters, numbers, and hyphens.`;
  }
  return "";
};

// GST validation
export const isGST = (value, field) => {
  const FieldName = formatFieldName(field);
  const gstRegex =
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
  if (!gstRegex.test(value)) {
    return `${FieldName} must be a valid GST number ex-(22ABCDE1234F2Z5).`;
  }
  return "";
};

// Pan validation
export const isPan = (value, field) => {
  const FieldName = formatFieldName(field);
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
  if (!panRegex.test(value)) {
    return `${FieldName} must be a valid Pan number ex-(ABCDE1234F).`;
  }
  return "";
};

export const isAlphanumeric = (value, field) => {
  const FieldName = formatFieldName(field);

  const formNameRegex = /^[A-Za-z0-9 ]+$/;

  const trimmedValue = value.trim();

  if (!formNameRegex.test(trimmedValue)) {
    return `${FieldName} must contain only letters and numbers.`;
  }
  if (/ {2,}/.test(value)) {
    return `${FieldName} cannot have multiple consecutive spaces.`;
  }

  if (value !== trimmedValue) {
    return `${FieldName} cannot have extra space. Add one more word after a space.`;
  }

  // if (value !== trimmedValue || / {2,}/.test(value)) {
  //   return `${FieldName} must not have extra spaces at the ends or multiple consecutive spaces.`;
  // }

  return "";
};

export const radioError = (value, field) => {
  const FieldName = formatFieldName(field);

  return !value || (typeof value === "string" && value?.trim() === "")
    ? `Selection is mandatory. Please choose one of the available options.`
    : "";
};
